import { FadeUp, Stagger, StaggerItem } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { useProjects } from "../hooks/useCms";
import { resolveImage } from "../data/site";
import { BedDouble, Bath, Ruler, MapPin } from "lucide-react";

const LAYOUT = [
  { span: "md:col-span-7", tall: false },
  { span: "md:col-span-5", tall: false },
  { span: "md:col-span-4", tall: true },
  { span: "md:col-span-4", tall: true },
  { span: "md:col-span-4", tall: false },
  { span: "md:col-span-7", tall: false },
  { span: "md:col-span-5", tall: false },
  { span: "md:col-span-7", tall: false },
  { span: "md:col-span-5", tall: false },
  { span: "md:col-span-12", tall: false },
];

const OurWork = () => {
  const projects = [...useProjects()].sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0) || (a.order || 0) - (b.order || 0));
  return (
  <main>
    <SEO />
    <PageHeader
      testid="our-work-header"
      eyebrow="Portfolio"
      lines={[{ text: "Our" }, { text: "Work", italic: true }]}
      description="A growing portfolio of completed homes across Idaho. Every project photographed is a family settling into a home built with intention."
    />

    {projects.length > 0 && (
      <section className="pb-20" data-testid="projects-section">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-16">
            {projects.map((p) => (
              <StaggerItem key={p.id}>
                <article data-testid={`project-${p.id}`}>
                  {p.images?.length > 0 && (
                    <div className="img-zoom aspect-[16/10] bg-[#EFECE7]">
                      <img src={resolveImage(p.images[0])} alt={`${p.name} — completed home by On Point Homes`} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="pt-6 border-b border-[rgba(26,26,26,0.12)] pb-8">
                    <div className="flex items-baseline justify-between gap-4">
                      <h2 className="font-serif text-3xl md:text-4xl tracking-tight">{p.name}</h2>
                      {p.year && <span className="font-mono text-sm font-semibold">{p.year}</span>}
                    </div>
                    {p.location && (
                      <p className="mt-2 flex items-center gap-2 text-sm text-[#5C5A56]"><MapPin size={14} className="text-[#8C6A4F]" /> {p.location}</p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-5 text-xs font-mono tracking-wider text-[#5C5A56] uppercase">
                      {p.beds != null && <span className="flex items-center gap-1.5"><BedDouble size={14} className="text-[#8C6A4F]" /> {p.beds} Bed</span>}
                      {p.baths != null && <span className="flex items-center gap-1.5"><Bath size={14} className="text-[#8C6A4F]" /> {p.baths} Bath</span>}
                      {p.sqft != null && <span className="flex items-center gap-1.5"><Ruler size={14} className="text-[#8C6A4F]" /> {p.sqft.toLocaleString()} SF</span>}
                    </div>
                    {p.description && <p className="mt-4 text-sm md:text-base text-[#5C5A56] leading-relaxed">{p.description}</p>}
                    {p.features?.length > 0 && (
                      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
                        {p.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-[#5C5A56]">
                            <span className="w-1 h-1 rotate-45 bg-[#8C6A4F]" /> {f}
                          </li>
                        ))}
                      </ul>
                    )}
                    {p.images?.length > 1 && (
                      <div className="mt-5 grid grid-cols-4 gap-2">
                        {p.images.slice(1, 5).map((img, i) => (
                          <a key={i} href={resolveImage(img)} target="_blank" rel="noreferrer" className="img-zoom aspect-[4/3] block" data-testid={`project-${p.id}-photo-${i}`}>
                            <img src={resolveImage(img)} alt={`${p.name} photo ${i + 2}`} className="w-full h-full object-cover" loading="lazy" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    )}

    <section className="pb-28 md:pb-40">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6" data-testid="portfolio-grid">
        {LAYOUT.map((p, i) => (
          <FadeUp key={i} delay={(i % 3) * 0.1} className={p.span}>
            <div className={`img-zoom relative ${p.tall ? "aspect-[3/4]" : "aspect-[16/10]"} bg-[#EFECE7]`}>
              <CyclingImage start={i * 3} interval={5000 + (i % 4) * 300} alt="Completed home built by On Point Homes, Idaho home builder" />
            </div>
          </FadeUp>
        ))}
      </div>
      <p className="max-w-[1600px] mx-auto px-5 md:px-10 mt-14 font-mono text-[0.65rem] tracking-[0.2em] uppercase text-[#A3A09B]">
        New projects added as they're completed.
      </p>
    </section>
  </main>
  );
};

export default OurWork;
